/* eslint-disable no-underscore-dangle */
import Vue from 'vue';
import {
    COMPONENT_TREE_ITEMS,
    COMPONENT_TREE_ITEM_NAMES,
    GET_COMPONENT_TREE_ITEM,
} from '@/store/types/getter-types';

import {
    ADD_SELECTED_ELEMENT,
    ADD_RECENT,
    CLEAR_COPIED_MENU_DATA,
    CLEAR_COPIED_RENDERING,
    CLEAR_DELETED_MENU_DATA,
    CLEAR_SELECTED_ELEMENT,
    CLEAR_SELECTED_ELEMENTS,
    CLEAR_SELECTED_LINK,
    CLEAR_SELECTED_RENDERING,
    HIDE_DRAWER,
    HIDE_MODAL,
    LOAD_TEMPLATE,
    MOUNT_FAVORITES,
    REMOVE_FAVORITE,
    REMOVE_SELECTED_ELEMENT,
    SELECT_PARENT_ELEMENT,
    SET_APP_THEME,
    SET_APP_THEME_LIST,
    SET_CAROUSEL_SPEED,
    SET_CHANGED_DATA,
    SET_CHANGED_OPTION,
    SET_COMPONENT_LIST,
    SET_COMPONENT_TREE,
    SET_CONTEXT_ELEMENT,
    SET_COPIED_MENU_DATA,
    SET_COPIED_ELEMENTS,
    SET_DELETED_MENU_DATA,
    SET_DEVICE_MODE,
    SET_DRAGGING_COMPONENT,
    SET_EDITOR_MODE,
    SET_LAST_COMPONENT_ID,
    SET_LAST_PAGE_ID,
    SET_PAGE_LOCK,
    SET_MEDIA_LIBRARY_TYPE,
    SET_NEW_PAGE_PATH,
    SET_PAGE_DATA,
    SET_CUT_CALLBACK,
    SET_PRESET_DATA,
    SET_PROTOTYPES,
    SET_RENDERINGS,
    SET_SELECTED_COMPONENT_METADATA,
    SET_SELECTED_COMPONENT,
    SET_SELECTED_CONTENT,
    SET_SELECTED_ELEMENT,
    SET_SELECTED_GROUP,
    SET_SELECTED_LINK,
    SET_SELECTED_MEDIA_TARGET,
    SET_SELECTED_MEDIA,
    SET_SELECTED_RENDERING,
    SET_SETTINGS_DATA,
    SET_TEMPLATES,
    SET_PRESELECTED_ITEM_ID,
    SET_WORKFLOW_STEPS,
    SHOW_ALERT,
    SHOW_DRAWER,
    SHOW_MODAL,
    TOGGLE_ADDING_COMPONENT,
    TOGGLE_ADDING_ORGANISMS,
    TOGGLE_FAVORITE,
    TOGGLE_GUIDED_SELECT,
    TOGGLE_PAGE_LOADING,
    TOGGLE_TOOLBAR,
    TRIGGER_PAGE_CREATED,
    UNLOCK_COMPONENT,
    SET_HOLD_EVENTS,
    MOVE_RENDERING,
    SET_VALIDATION_ERRORS,
    CLEAR_VALIDATION_ERRORS,
} from '@/store/types/mutation-types';

import {
    CREATE_COMPONENT,
    EXECUTE_WORKFLOW,
    LOAD_COMPONENT_TREE,
    LOAD_PAGE,
    LOAD_PRESETS,
    LOAD_PROTOTYPES,
    LOAD_SETTINGS,
    LOAD_TEMPLATES,
    LOAD_WORKFLOW_STEPS,
    PUBLISH_ITEM,
    SAVE_LANGUAGE_VERSION,
    SAVE_PAGE,
    SAVE_COMPONENT,
    SAVE_SETTINGS,
    TOGGLE_LOCK,
} from '@/store/types/action-types';

import getParentElement from '@/utils/get-parent-element';
import { deviceClasses } from '@/utils/devices';

import * as service from '@/sitecore/sitecore-data-service';
import { processData, encodeData, encodeRendering } from '@/data/data';
import { STATE_CLASSES } from '@/data/constants/classes';

import * as sampleComponentDataJson from '@/sample-data/component-tree-data.json';
import * as atomDataJson from '@/data/menu/atoms.json';
import * as atomDataClientJson from '@/client/data/menu/atoms.json';
import * as structuralDataJson from '@/data/menu/structural.json';
import * as standardDataJson from '@/data/menu/standard.json';
import * as standardDataClientJson from '@/client/data/menu/standard.json';
import * as formDataJson from '@/data/menu/form.json';

const pageState = {
    state: {
        pageData: null,
        workflowSteps: null,
        presets: null,
        pageLoading: { loading: true },
        deviceMode: null,
        editorMode: 'insert',
        guidedMode: false,
        visibleMode: true,
        moving: false,
        components: [],
        draggingComponent: false,

        contextElement: null,
        selectedElements: [],
        selectedRendering: null,
        selectedRenderingUid: '',
        selectedComponent: null,
        selectedComponentMetadata: null,
        renderings: [],
        updatedCarouselSpeed: null,
        onCutCallback: () => { },
        drawer: null,
        modal: null,

        options: null,
        changedOption: null,
        copiedMenuData: null,
        deletedMenuData: null,
        copiedElements: {
            pageId: '',
            mode: '',
            elements: [],
        },
        selectedLink: {},

        toolbarVisibility: {},
        addingComponent: false,
        addingOrganisms: true,
        parentRendering: null,

        changedData: false,
        ignoreChangedData: false,

        selectedContent: {},
        selectedMedia: {},
        selectedMediaTarget: '',
        mediaLibraryType: 'image',
        preselectedItemId: '',

        templates: [],
        prototypes: [],
        newPagePath: '/sitecore/content/home',
        favorites: [],
        recents: [],
        componentTree: [],
        componentList: [],

        appTheme: 'default',
        appThemeList: ['default'],
        lastPageId: null,
        lastComponentId: null,
        pageCreated: 0,

        holdEvents: false,
        validationErrors: [], // [ { element: {}, messages: [] }]
    },

    getters: {
        [COMPONENT_TREE_ITEMS](state) {
            const items = [];

            state.componentTree.forEach((section) => {
                section.items.forEach((category) => {
                    if (!category.children) return;

                    category.children.forEach((item) => {
                        items.push(item);
                    });
                });
            });

            return items;
        },

        [COMPONENT_TREE_ITEM_NAMES](state, getters) {
            return getters[COMPONENT_TREE_ITEMS].map((item) => {
                const { displayName, children } = item;

                return children && children.length ? [displayName].concat(children.map(child => child.displayName)) : displayName;
            }).flat();
        },

        [GET_COMPONENT_TREE_ITEM](state) {
            return (query = {}) => {
                let foundItem = null;

                state.componentTree.forEach((section) => {
                    section.items.forEach((category) => {
                        if (!category.children) return;

                        category.children.forEach((item) => {
                            Object.keys(query).forEach((key) => {
                                if (item[key] === query[key]) {
                                    foundItem = item;
                                }
                            });
                        });
                    });
                });

                return foundItem;
            };
        },
    },

    mutations: {
        [SET_HOLD_EVENTS](state, status) {
            state.holdEvents = status;
        },

        [SET_LAST_COMPONENT_ID](state, id) {
            state.lastComponentId = id;
        },

        [SET_WORKFLOW_STEPS](state, steps) {
            state.workflowSteps = steps;
        },

        [TRIGGER_PAGE_CREATED](state) {
            state.pageCreated++;
        },

        [SET_COMPONENT_TREE](state, tree) {
            state.componentTree = tree;
        },

        [SET_COMPONENT_LIST](state, componentList) {
            state.componentList = componentList;
        },

        [SET_LAST_PAGE_ID](state, id) {
            state.lastPageId = id;
        },

        [SET_PAGE_DATA](state, page) {
            state.pageData = page;
        },

        [SET_PRESET_DATA](state, presets) {
            state.presets = presets;
        },

        [SET_SELECTED_ELEMENT](state, { element }) {
            const { selectedElements } = state;

            selectedElements.forEach((selectedElement) => {
                selectedElement.isSelected = false;
                selectedElement.boxModelDisplayed = false;
            });

            element.isSelected = true;
            state.selectedElements = [element];
        },

        [CLEAR_SELECTED_ELEMENT](state, index) {
            // this needs to change to search by id
            const theElement = state.selectedElements[index];
            theElement.isSelected = false;
            theElement.boxModelDisplayed = false;
            state.selectedElements.splice(index, 1);
        },

        [CLEAR_SELECTED_ELEMENTS](state) {
            const { selectedElements } = state;

            selectedElements.forEach((selectedElement) => {
                selectedElement.isSelected = false;
                selectedElement.boxModelDisplayed = false;
            });

            state.selectedElements = [];
        },

        [ADD_SELECTED_ELEMENT](state, { element }) {
            const { selectedElements } = state;
            const uids = selectedElements.map(el => el._uid);

            if (!uids.includes(element._uid)) {
                selectedElements.forEach((selectedElement) => {
                    selectedElement.boxModelDisplayed = false;
                });

                selectedElements.push(element);
                element.isSelected = true;
            }
        },

        [REMOVE_SELECTED_ELEMENT](state, { element }) {
            const { selectedElements } = state;
            const elementIndex = selectedElements.findIndex(el => element._uid === el._uid);

            if (elementIndex > -1) {
                element.isSelected = false;
                selectedElements.splice(elementIndex, 1);
            }
        },

        [SET_SELECTED_GROUP](state, { element }) {
            const group = Array.from(element.group);
            const { selectedElements } = state;
            const isNewGroup = !group.includes(state.selectedElements[0]);

            if (element.isSelected) {
                // if the current element is already selected, deselect the other group elements
                group.forEach((groupElem) => {
                    if (groupElem !== element) groupElem.isSelected = false;
                });

                state.selectedElements = [element];
            } else {
                // if the current element isn't already selected, select the entire group
                group.forEach((groupElem) => {
                    groupElem.isSelected = true;
                });

                // if this is a new group, then deselect all of the elements in the previously selected group
                if (isNewGroup) {
                    selectedElements.forEach((selectedElement) => {
                        selectedElement.isSelected = false;
                    });
                }

                state.selectedElements = group;
            }
        },

        [SET_CONTEXT_ELEMENT](state, { element }) {
            state.contextElement = element;
        },

        [SET_SELECTED_RENDERING](state, { rendering }) {
            state.selectedRendering = rendering;
            state.selectedRenderingUid = rendering.id;
        },

        [CLEAR_SELECTED_RENDERING](state) {
            state.selectedRendering = null;
        },

        [SELECT_PARENT_ELEMENT](state) {
            let currentElement = state.selectedElements[0];

            // @TODO - show an error to the user, or better yet hide the up arrow
            if (currentElement.length > 1 || !currentElement.$parent) return;

            currentElement = getParentElement(currentElement);

            if (!currentElement) return;

            state.selectedElements.forEach((selectedElement) => {
                selectedElement.isSelected = false;
                selectedElement.boxModelDisplayed = false;
            });

            const group = Array.from(currentElement.group);

            if (group.length) {
                group.forEach((groupElem) => {
                    groupElem.isSelected = true;
                });

                state.selectedElements = group;
            } else {
                currentElement.isSelected = true;
                state.selectedElements = [currentElement];
            }
        },

        [SET_SELECTED_COMPONENT](state, component) {
            state.selectedComponent = component;
        },

        [SET_SELECTED_COMPONENT_METADATA](state, metadata) {
            state.selectedComponentMetadata = metadata;
        },

        [SET_DEVICE_MODE](state, opts) {
            state.deviceMode = opts;

            const { deviceClass } = state.deviceMode;
            const { classList } = document.body;

            Object.values(deviceClasses).forEach(className => classList.remove(className));

            classList.add(deviceClass);
        },

        [SET_EDITOR_MODE](state, editorMode) {
            state.editorMode = editorMode;
        },

        [SET_COPIED_ELEMENTS](state, { mode, pageId, elements }) {
            state.copiedElements = {
                mode,
                pageId,
                elements: [...elements],
            };

            state.moving = true;
        },

        [SET_COPIED_MENU_DATA](state, menuData) {
            state.copiedMenuData = menuData;
        },

        [SET_DELETED_MENU_DATA](state, menuData) {
            state.deletedMenuData = menuData;
        },

        [SET_CAROUSEL_SPEED](state, { el, speed }) {
            state.updatedCarouselSpeed = {
                el,
                speed,
            };
        },

        [SET_CUT_CALLBACK](state, callback) {
            state.onCutCallback = () => {
                callback();
                state.onCutCallback = () => {};
            };
        },

        [SET_PAGE_LOCK](state, status) {
            state.pageData.pageData.locked = status;
            if (status) {
                state.pageData.pageData.lockedby = state.user.username;
            }
        },

        [CLEAR_COPIED_RENDERING](state) {
            state.copiedElements = {
                pageId: 'guid',
                mode: 'copy',
                elements: [],
            };

            state.moving = false;
        },

        [CLEAR_COPIED_MENU_DATA](state) {
            state.copiedMenuData = null;
        },

        [CLEAR_DELETED_MENU_DATA](state) {
            state.deletedMenuData = null;
        },

        [SET_SELECTED_LINK](state, newLink) {
            state.selectedLink = newLink;
        },

        [CLEAR_SELECTED_LINK](state) {
            state.selectedLink = {};
        },

        // TODO: Implement actual template loading
        [LOAD_TEMPLATE](state, { renderingData }) {
            state.renderings = renderingData.slice(0);
        },

        [MOVE_RENDERING](state, { index, to, element }) {
            state.renderings.splice(index, 1);
            state.renderings.splice(to, 0, element);
        },

        [SET_RENDERINGS](state, renderings) {
            state.renderings = renderings;
        },

        [SET_TEMPLATES](state, templates) {
            state.templates = templates;
        },

        [SET_PROTOTYPES](state, prototypes) {
            state.prototypes = prototypes;
        },

        [SET_NEW_PAGE_PATH](state, newPagePath) {
            state.newPagePath = newPagePath;
        },

        [SET_SELECTED_MEDIA](state, item) {
            state.selectedMedia = {
                id: item.id,
                name: item.name,
                mediaType: item.mediaType,
                url: item.url,
                altText: item.altText,
                extension: item.extension,
                height: item.height,
                width: item.width,
                chid: item.chid,
            };
        },

        [SET_SELECTED_MEDIA_TARGET](state, target) {
            state.selectedMediaTarget = target;
        },

        [SET_PRESELECTED_ITEM_ID](state, id) {
            state.preselectedItemId = id;
        },

        [SET_MEDIA_LIBRARY_TYPE](state, type) {
            state.mediaLibraryType = type;
        },

        [SET_SELECTED_CONTENT](state, item) {
            state.selectedContent = { id: item.id, url: item.url, name: item.name };
        },

        [TOGGLE_ADDING_COMPONENT](state, isAdding) {
            state.addingComponent = isAdding;
        },

        [SET_DRAGGING_COMPONENT](state, isDragging) {
            state.draggingComponent = isDragging;
        },

        [TOGGLE_ADDING_ORGANISMS](state, isAdding) {
            state.addingOrganisms = isAdding;
        },

        [TOGGLE_GUIDED_SELECT](state, forceToggle) {
            function toggleHighlighted(element, guidedMode) {
                if (!element) return;

                const editorClassSet = new Set(element.editorClasses);

                element.isHighlighted = element.isSelectableNew ? guidedMode : false;
                element.boxModelDisplayed = false;

                if (guidedMode) {
                    document.body.classList.add(STATE_CLASSES.GUIDED);
                    editorClassSet.add(STATE_CLASSES.HIGHLIGHTED);
                } else {
                    document.body.classList.remove(STATE_CLASSES.GUIDED);
                    editorClassSet.delete(STATE_CLASSES.HIGHLIGHTED);
                }

                element.editorClasses = Array.from(editorClassSet);

                element.$children.forEach((childElem) => {
                    toggleHighlighted(childElem, guidedMode);
                });
            }

            const guidedMode = typeof forceToggle !== 'undefined' ? forceToggle : !state.selectedRendering.isHighlighted;
            state.guidedMode = guidedMode;

            toggleHighlighted(state.selectedRendering, guidedMode);
        },

        [TOGGLE_PAGE_LOADING](state, { loading, message }) {
            state.pageLoading = { loading, message };
        },

        [SET_CHANGED_DATA](state, status) {
            state.changedData = status;
        },

        [SET_VALIDATION_ERRORS](state, { element, messages }) {
            const index = state.validationErrors.findIndex(i => i.element._uid === element._uid);
            const remove = !messages || messages.length < 1;

            if (index > -1) {
                if (remove) {
                    state.validationErrors.splice(index, 1);
                } else {
                    state.validationErrors.splice(index, 1, { element, messages });
                }
            } else if (!remove) {
                state.validationErrors.push({ element, messages });
            }
        },

        [CLEAR_VALIDATION_ERRORS](state) {
            state.validationErrors = [];
        },

        [SET_CHANGED_OPTION](state, status) {
            state.changedOption = status;
        },

        [ADD_RECENT](state, recent) {
            const obj = state.recents.find(o => o.id === recent.id);
            if (obj !== undefined) {
                obj.name = recent.name;
                return;
            }
            if (state.recents.length > 4) {
                state.recents.pop();
            }
            state.recents.unshift(recent);
            localStorage.setItem('flexRecents', JSON.stringify(state.recents));
        },

        [TOGGLE_FAVORITE](state, favorite) {
            const obj = state.favorites.find(o => o.id === favorite.id);
            if (obj !== undefined) {
                const index = state.favorites.indexOf(obj);
                state.favorites.splice(index, 1);
                return;
            }
            state.favorites.push(favorite);
            localStorage.setItem('flexFavorites', JSON.stringify(state.favorites));
        },

        [REMOVE_FAVORITE](state, favorite) {
            const obj = state.favorites.find(o => o.id === favorite.id);
            const index = state.favorites.indexOf(obj);
            state.favorites.splice(index, 1);
            localStorage.setItem('flexFavorites', JSON.stringify(state.favorites));
        },

        [MOUNT_FAVORITES](state) {
            const favs = JSON.parse(localStorage.getItem('flexFavorites'));
            if (favs && favs.length !== 0) {
                state.favorites = favs;
            }
            const recents = JSON.parse(localStorage.getItem('flexRecents'));
            if (recents && recents.length !== 0) {
                state.recents = recents;
            }
        },

        [UNLOCK_COMPONENT](state) {
            state.selectedRendering.linkedComponentNew = '';
            state.selectedRendering.lockComponentNew = false;
        },

        [TOGGLE_TOOLBAR](state, { id, isVisible }) {
            if (typeof state.toolbarVisibility[id] === 'undefined') {
                Vue.set(state.toolbarVisibility, id, false);
            }

            state.toolbarVisibility[id] = typeof isVisible !== 'undefined' ? isVisible : !state.toolbarVisibility[id];
        },

        [SET_APP_THEME](state, appThemeNew) {
            if (!state.appThemeList.includes(appThemeNew)) return;

            state.appThemeList.forEach((appTheme) => {
                document.body.classList.remove(`is-theme-${ appTheme }`);
            });

            document.body.classList.add(`is-theme-${ appThemeNew }`);

            state.appTheme = appThemeNew;
            localStorage.setItem('flexAppTheme', appThemeNew);
        },

        [SET_APP_THEME_LIST](state, themeList) {
            state.appThemeList = themeList;
        },

        [SHOW_DRAWER](state, args) {
            let componentTag;
            let data;

            if (typeof args === 'object') {
                componentTag = args.componentTag;
                data = args.data;
            } else {
                componentTag = args;
            }

            if (!componentTag) {
                console.warn('Unable to display the drawer. The component tag is missing.');
                return;
            }

            state.drawer = { componentTag, data };
        },

        [HIDE_DRAWER](state) {
            state.drawer = null;
        },

        [SHOW_MODAL](state, args) {
            let componentTag;
            let data;

            if (typeof args === 'object') {
                componentTag = args.componentTag;
                data = args.data;
            } else {
                componentTag = args;
            }

            if (!componentTag) {
                console.warn('Unable to display the modal. The component tag is missing.');
                return;
            }

            state.modal = { componentTag, data };
        },

        [HIDE_MODAL](state) {
            state.modal = null;
        },
    },

    actions: {
        // TODO: set get page when data is ready
        async [LOAD_PAGE]({ state, commit, dispatch }, pageInfo) {
            if (pageInfo.loading) {
                commit(TOGGLE_PAGE_LOADING, true);
            }

            const response = await service.getPage(pageInfo.pageId, pageInfo.lang || 'en');

            // friendly catch for API errors
            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });

                if (state.pageLoading.loading) {
                    commit(TOGGLE_PAGE_LOADING, false);
                }

                return;
            }

            // general catch for logic errors in code
            try {
                const processedData = processData(response.data);
                commit(SET_PAGE_DATA, processedData);
                commit(SET_CHANGED_DATA, false);

                if (processedData.pageData.itemPath && !processedData.pageData.itemPath.startsWith('/sitecore/templates')) {
                    const newRecent = { id: processedData.pageData.id, name: processedData.pageData.itemName };
                    commit(ADD_RECENT, newRecent);
                }

                dispatch(LOAD_WORKFLOW_STEPS, processedData.pageData.id);
            } catch {
                commit(SHOW_ALERT, { description: 'Error in processing page data' });
            }
            if (pageInfo.loading) {
                commit(TOGGLE_PAGE_LOADING, false);
            }
        },

        async [SAVE_PAGE]({ state, commit }, mode = 'page') {
            commit(SET_CHANGED_DATA, false);
            const updatedPageData = { ...state.pageData };
            updatedPageData.layout.placeholders.main = state.renderings;
            const encodedPage = encodeData(updatedPageData);
            let response = {};
            if (mode === 'templates') {
                response = await service.postTemplate(encodedPage);
            } else {
                response = await service.postPage(encodedPage);
            }
            if (response.status === service.ERROR) {
                if (String(response.error).includes('403')) {
                    commit(SHOW_ALERT, {
                        description: 'Error authenticating with Sitecore. In 5 seconds, a new signin window will open to refresh your authentication. Please try saving again after.',
                    });

                    window.setTimeout(() => {
                        window.location.href = '/signin';
                    }, 6000);

                    return;
                }

                commit(SHOW_ALERT, { description: response.error });
            }
        },

        async [CREATE_COMPONENT]({ commit }, { component, componentData, inBackground }) {
            let response = {};
            const encodedComponent = encodeRendering(component, componentData, true);

            if (!inBackground) commit(SET_CHANGED_DATA, false);

            response = await service.createComponent(encodedComponent);

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return false;
            }

            if (!inBackground) commit(TOGGLE_ADDING_COMPONENT, false);

            return response;
        },

        async [SAVE_COMPONENT]({ state, commit, dispatch }, componentData) {
            let response = {};
            const encodedComponent = encodeRendering(state.selectedComponent, componentData, false);

            commit(SET_CHANGED_DATA, false);
            response = await service.saveComponent(encodedComponent);

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return false;
            }

            commit(TOGGLE_ADDING_COMPONENT, false);
            // reload page data
            let lang;
            if (state.pageData && state.pageData.pageData.language !== 'en') {
                lang = state.pageData.pageData.language;
            }
            if (state.pageData) {
                dispatch(LOAD_PAGE, { pageId: state.pageData.pageData.id, lang, loading: false });
            }

            return true;
        },

        async [LOAD_COMPONENT_TREE]({ commit }) {
            const response = await service.getComponentLibrary();

            let treeData = [];

            // Error from server, allow fallthrough to set standard component menu
            if (response.status === 'error' || !response.data || !response.data.menu) {
                console.warn('Error retrieving menu data from Sitecore, using fallback menu.');

                if (response.error) {
                    commit(SHOW_ALERT, { description: response.error });
                } else {
                    commit(SHOW_ALERT, {
                        description: 'Error retrieving menu data from Sitecore, using fallback menu.',
                    });
                }

                treeData = sampleComponentDataJson.default.menu;

                return;
            }

            function deepCopy(data) {
                if (!data) return null;

                return JSON.parse(JSON.stringify(data));
            }

            function mergeMenus(flexData, clientData) {
                const flexItems = deepCopy(flexData.default.items);
                const flexItemNames = flexItems.map(i => i.name);
                const clientItems = deepCopy(clientData.default.items) || [];

                clientItems.forEach((clientItem) => {
                    if (flexItemNames.includes(clientItem.name) && clientItem.children) {
                        // if a folder already exists and it has items, merge the flex and client items together
                        const flexItem = flexItems.find(i => i.name === clientItem.name);

                        flexItem.children = [
                            ...flexItem.children,
                            ...clientItem.children,
                        ];
                    } else {
                        // otherwise, push the item to the end of the array
                        flexItems.push(clientItem);
                    }
                });

                flexItems.sort((a, b) => {
                    return a.displayName < b.displayName ? -1 : 1;
                });

                return {
                    ...flexData.default,
                    name: clientData.default.name || flexData.default.name,
                    icon: clientData.default.icon || flexData.default.icon,
                    items: flexItems,
                };
            }

            const mergedStandardData = mergeMenus(standardDataJson, standardDataClientJson);
            const mergedAtomData = mergeMenus(atomDataJson, atomDataClientJson);

            // push in manually controlled components
            treeData = response.data.menu;
            treeData.push(mergedStandardData);
            treeData.push(formDataJson.default);
            treeData.push(structuralDataJson.default);
            treeData.push(mergedAtomData);
            commit(SET_COMPONENT_TREE, treeData);
        },

        async [SAVE_LANGUAGE_VERSION]({ state, commit }, { mode, lang }) {
            commit(SET_CHANGED_DATA, false);
            const updatedPageData = { ...state.pageData };
            updatedPageData.pageData.language = lang;

            // function blankDatasource(obj) {
            //     if (obj.dataSourceId) {
            //         obj.dataSourceId = '';
            //     } else {
            //         obj.id = uuidv4();
            //     }

            //     if (obj.elements) {
            //         obj.elements.forEach((element) => {
            //             blankDatasource(element);
            //         });
            //     }
            // }

            // updatedPageData.layout.placeholders.main.forEach((r) => {
            //     blankDatasource(r);
            // });

            const encodedPage = encodeData(updatedPageData);
            let response = {};
            if (mode === 'templates') {
                response = await service.postTemplate(encodedPage);
            } else {
                response = await service.postPage(encodedPage);
            }

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }
        },

        async [LOAD_PRESETS]({ commit }) {
            const response = await service.getPresets();

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }

            commit(SET_PRESET_DATA, response.data);
        },

        async [SAVE_SETTINGS]({ commit }, settings) {
            const response = await service.postSettings(settings);

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }

            commit(SET_SETTINGS_DATA, settings);
        },

        async [LOAD_SETTINGS]({ commit }) {
            const response = await service.loadSettings();

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }

            commit(SET_SETTINGS_DATA, response.data);
        },

        async [LOAD_TEMPLATES]({ commit }) {
            const response = await service.getTemplates();

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }

            const templates = response.data;

            commit(SET_TEMPLATES, templates);
        },

        async [LOAD_PROTOTYPES]({ commit }) {
            const response = await service.getPrototypes();
            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }
            const prototypes = response.data;
            commit(SET_PROTOTYPES, prototypes);
        },

        async [EXECUTE_WORKFLOW]({ commit, dispatch, state }, { itemId, commandId, comment }) {
            const response = await service.executeWorkflow(itemId, commandId, comment);

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }

            state.pageData.pageData.workflow.state = response.data.state;
            state.pageData.pageData.locked = response.data.locked;
            state.pageData.pageData.lockedby = response.data.lockedby;

            dispatch(LOAD_WORKFLOW_STEPS, itemId);
        },

        async [LOAD_WORKFLOW_STEPS]({ commit }, itemid) {
            const response = await service.getWorkflowSteps(itemid);

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }

            commit(SET_WORKFLOW_STEPS, response.data);
        },

        async [PUBLISH_ITEM]({ commit }, { itemid, language, publishingTargets }) {
            const response = await service.publishItem(itemid, language, publishingTargets);

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }
        },

        async [TOGGLE_LOCK]({ commit }, itemid) {
            const response = await service.toggleLock(itemid);

            if (response.status === service.ERROR) {
                commit(SHOW_ALERT, { description: response.error });
                return;
            }
            commit(SET_PAGE_LOCK, response.data);
        },
    },
};

export default pageState;
